import React from "react";
import styled, { css } from "styled-components";
import { Dispatch } from "redux";
import { animated, useSpring, config } from "react-spring";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faComments } from "@fortawesome/free-solid-svg-icons";

import {
  startReactoin,
  AddMessageAction,
  ReplyMessageAction,
  getChildNodes,
  InsertConversationLog,
  ReturnRootMessage,
  EndReaction,
  EndMessage
} from "../store/chatmessage/actions";
import { Sender, IMessage, INode, ChatImageTarget } from "../store/chatmessage/types";
import { chatTheme } from "../styles/theme";
import { insertLine, sleep } from "./util/index";

import { IChatSettingState } from "../store/chatSetting/types";
import LikeBtn from "./LikeBtn";
import Returnbtn from "./ReturnBtn";
import RetrunPrevQuestionBtn from "./RetrunPrevQuestionBtn";

const ChatMessageContainer = styled(animated.div)<{ margin: string }>`
  display: flex;
  margin: ${(props: ComponentProps) => props.margin};
`;

const ChatMessageBody = styled.div<{ type: string, color: string }>`
  min-width: 45%;
  width: fit-content;
  max-width: 80%;
  height: auto;
  height: fit-content;
  border-radius: ${chatTheme.borderRadius};
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.18);
  color: ${chatTheme.font.primary};
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  padding: 10px;
  position: relative;
  background: ${props => props.color};

  ${props =>
    props.type === Sender.OPERATOR
      ? css`
          &:before {
            content: "";
            display: inline-block;
            position: absolute;
            top: -9px;
            left: -18px;
            border: 8px solid transparent;
            border-right: 18px solid ${props.color};
            -ms-transform: rotate(35deg);
            -webkit-transform: rotate(35deg);
            transform: rotate(35deg);
          }
        `
      : css``}
`;

const MessageTitle = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const QuestionWrap = styled.div`
  margin-top: 10px;
  min-width: 90%;
  width: 90%;
  width: fit-content;
`;
const SelectBtn = styled.div<{ color: string }>`
  width: 100%;
  word-break: break-all;
	padding: 5px;
	font-size: 13px;
	margin: 10px 0;
	cursor: pointer;
	color: #fff;
	background: ${props => props.color};
	border: 1px solid ${props => props.color};
	border-radius: 5px;
	transition: 0.3s ease-out;

	&:hover {
		background transparent;
		color: ${props => props.color};
	}
`;

const Operator = styled.div<{ backImage: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid black;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  ${props =>
    props.backImage
      ? "background: url(" + props.backImage + ") center/cover no-repeat;"
      : ""}
`;

const OperatorText = styled.div`
  word-break: break-all;
`;

const CustomerText = styled.span`
  word-break: break-all;
`;

const ChatImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
`;

interface ComponentProps {
  margin: string;
}

interface IProps {
  data: IMessage;
  setting: IChatSettingState;
  chatBotData: any;
  dispatch: Dispatch;
}

const ChatMessage: React.FC<IProps> = ({
  data,
  dispatch,
  setting,
  chatBotData
}) => {
  let messagePosition = "flex-start";
  let space = "";
  let OperatorSelect;
  const chatBotBaseData = chatBotData
  const childData = getChildNodes(data.chatdata_id)
  const messageType =
    data.chatdata_id !== "" &&
    "type" in Object(chatBotBaseData[data.chatdata_id])
      ? chatBotBaseData[data.chatdata_id].type
      : null;

  const animation = useSpring({
    config: {
      ...config.default
    },
    from: {
      transform:
        data.type === Sender.OPERATOR
          ? "translate(-12%, 0)"
          : "translate(12%, 0)"
    },
    to: {
      transform:
        data.type === Sender.OPERATOR ? "translate(0, 0)" : "translate(0, 0)"
    }
  });

  const loading = false; // loading状態を作る時に使う
  if (loading) {
    return (
      <ChatMessageContainer
        style={{ justifyContent: messagePosition }}
        margin={space}
        color={setting.mainColor}
      >
        <ChatMessageBody color={setting.subColor} type={data.type}>
          <MessageTitle>
            <FontAwesomeIcon icon={faSpinner} size="2x" />
          </MessageTitle>
        </ChatMessageBody>
      </ChatMessageContainer>
    );
  }

  async function dalayMessage(answer: INode) {
    await sleep(1200);
    dispatch(ReplyMessageAction(answer));
    if (answer.type === "OPERATOR") {
      dispatch(InsertConversationLog(answer.id));
    }
    if (
      answer.type === "OPERATOR_ANSWER" &&
      answer.children.length === 0 &&
      "destinationId" in answer &&
      typeof answer.destinationId !== "undefined" &&
      answer.destinationId !== null
    ) {
      if (
        chatBotBaseData[answer.destinationId] &&
        chatBotBaseData[answer.destinationId].type === "OPERATOR"
      ) {
        const message = chatBotBaseData[answer.destinationId];
        message.prev_id = answer.id;
        dalayMessage(message);
      } else {
        await sleep(1600);
        dispatch(ReturnRootMessage());
      }
    } else if (
      answer.type === "OPERATOR_ANSWER" &&
      answer.children.length === 0 &&
      "endMessage" in answer &&
      typeof answer.endMessage !== "undefined" &&
      answer.endMessage !== ""
    ) {
      await sleep(1600);
      dispatch(EndMessage(answer.endMessage));
    } else if (
      answer.type === "OPERATOR_ANSWER" &&
      answer.children.length === 0
    ) {
      await sleep(1600);
      dispatch(ReturnRootMessage());
    } else if (
      // 回答が続く場合
      answer.type === "OPERATOR_ANSWER" &&
      answer.children.length > 0
    ) {
      const childAnswers = getChildNodes(answer.id);
      const childAnswer = childAnswers[0];
      if (childAnswer && childAnswer.type === "OPERATOR_ANSWER") {
        childAnswer.prev_id = answer.id;
        dalayMessage(childAnswer);
      }
    }
  }
  const ReactionProcess = (data: IMessage) => {
    dispatch(startReactoin());
    dispatch(InsertConversationLog(data.chatdata_id));
    dispatch(AddMessageAction(data));

    const answers = getChildNodes(data.chatdata_id);
    let answer = answers[0]; // 今の仕様だと答えが複数のパターンはないので答えは1つめを使う
    if (answer) {
      answer.prev_id = data.chatdata_id;
      dalayMessage(answer);
    }
    dispatch(EndReaction(data));
  };
  const questionList = childData.length !== 0 && (
    <>
      {childData.map((item: INode, index: number) => {
        if (typeof item !== "undefined" && item.type === Sender.CUSTOMER) {
          const cdata: IMessage = {
            message_id: "",
            chatdata_id: item.id,
            type: Sender.CUSTOMER,
            text: item.content
          };
          return (
            <SelectBtn
              key={index}
              color={setting.mainColor}
              onClick={() => ReactionProcess(cdata)}                                      
            >
              {item.content}
            </SelectBtn>
          );
        }
        return null;
      })}
    </>
  );

  switch (data.type) {
    case Sender.OPERATOR:
      messagePosition = "flex-start";
      space = "20px 0 30px 10px";
      if (questionList) {
        OperatorSelect = <QuestionWrap>{questionList}</QuestionWrap>;
      }
      break;
    case Sender.CUSTOMER:
      messagePosition = "flex-end";
      space = "20px 10px 30px -20px";
      break;
    default:
      break;
  }

  const Message = (type: string, text: string) => {
    const outputText = insertLine(text);
    switch (type) {
      case Sender.OPERATOR:
        return (
          <OperatorText data-testid="operator-text">{outputText}</OperatorText>
        );
      case Sender.CUSTOMER:
        return (
          <CustomerText data-testid="customer-text">{outputText}</CustomerText>
        );
      default:
        return null;
    }
  };

  // 改行を反映するため
  const outputData = Message(data.type, data.text);

  return (
    <React.Fragment>
      <animated.div style={animation}>
        <ChatMessageContainer
          style={{ justifyContent: messagePosition }}
          margin={space}
        >
          {data.type === Sender.OPERATOR && (
            <div style={{ display: "flex", marginRight: "15px" }}>
              <Operator backImage={setting.iconImage}>
                {!setting.iconImage && <FontAwesomeIcon icon={faComments} />}
              </Operator>
            </div>
          )}
          <ChatMessageBody
            color={setting.subColor}
            type={data.type}
            style={{ marginTop: "30px" }}
          >
          {data.type === "OPERATOR" &&
            data.chatImages &&
            data.chatdata_id !== "element-root" && (
              data.chatImages.filter((image: any, index: number) => {
                return image.position === "A";
              }).map((image: any, index: number) => {
                if (image.hasLink && image.link !== "") {
                  const target = image.target === ChatImageTarget.NEW ? "_blank" : "_self";
                  return (
                    <a key={index} href={image.link} target={target} style={{ marginBottom : 10 }}>
                      <ChatImage alt={image.url} src={image.url}/>
                    </a>
                  ); 
                } else {
                  return <ChatImage key={index} alt={image.url} src={image.url} style={{ marginBottom : 10 }}/>
                }
              })
            )}
            <MessageTitle>{outputData}</MessageTitle>
            {OperatorSelect}

            {data.type === "OPERATOR" &&
            data.chatImages &&
            data.chatdata_id !== "element-root" && (
              data.chatImages.filter((image: any, index: number) => {
                return image.position === "B";
              }).map((image: any, index: number) => {
                if (image.hasLink && image.link !== "") {
                  const target = image.target === ChatImageTarget.NEW ? "_blank" : "_self";
                  return (
                    <a key={index} href={image.link} target={target} style={{ marginBottom : 10 }}>
                      <ChatImage alt={image.url} src={image.url}/>
                    </a>
                  ); 
                } else {
                  return <ChatImage key={index} alt={image.url} src={image.url} style={{ marginBottom : 10 }}/>
                }
              })
            )}
            {data.like_btn === true && <LikeBtn/>}
            {data.return_btn === true && (
              <Returnbtn color={setting.mainColor} isEnd={true}/>
            )}
            {data.type === "OPERATOR" &&
              messageType === "OPERATOR" &&
              data.prev_id !== "" &&
              data.chatdata_id !== "element-root" && (
                <RetrunPrevQuestionBtn
                  dispatch={dispatch}
                  color={setting.mainColor}
                  id={data.chatdata_id}
                  prev_id={data.prev_id || ''}
                  chatBotBaseData={chatBotBaseData}
                />
              )}
            {data.type === "OPERATOR" &&
              messageType === "OPERATOR" &&
              data.chatdata_id !== "element-root" &&
              data.return_btn !== true && (
                <Returnbtn color={setting.mainColor} isEnd={false}/>
            )}
          </ChatMessageBody>
        </ChatMessageContainer>
      </animated.div>

      {childData.map((item: INode, index: number) => {
        const cdata: IMessage = {
          message_id: item.id,
          chatdata_id: item.id,
          type: Sender.OPERATOR,
          text: item.content
        };
        if (item.type === Sender.OPERATOR) {
          return (
            <ChatMessage
              key={cdata.message_id}
              dispatch={dispatch}
              setting={setting}
              data={cdata}
              chatBotData={chatBotBaseData}
            />
          );
        }
        return null;
      })}
    </React.Fragment>
  );
};

export default React.memo(ChatMessage);
